import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useState } from 'react';
import {
    H6Emphasized,
    Notice,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { TextEditor } from '../../common/components/GridCellEditors';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { type MemberDetails } from '../../entities/entityTypes';
import { useUpdateMemberAndRefetch } from '../../member/hooks/useUpdateMember';

const PII_UPDATE_WARNING =
    'This will update the official identity record in Synctera. This action should only be taken if the proper identity verification steps have been completed.';

interface MemberPIIRecordProps {
    members: MemberDetails[];
}

interface PendingPIIUpdate {
    memberId: string;
    legalFirstName: string | null;
    legalLastName: string | null;
}

export const MemberPIIRecord = ({ members }: MemberPIIRecordProps) => {
    const toast = useToastController();
    const adminUser = useAtomValue(adminUserAtom);
    const canEditMemberPii =
        adminUser.authorization.selectedMembership.pii.canEdit;
    const membership = useAtomValue(selectedMembershipAtom);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState<PendingPIIUpdate | null>(
        null,
    );
    const [showPIIWarningNotice, setShowPIIWarningNotice] = useState(false);

    const { updateMemberAsync } = useUpdateMemberAndRefetch(membership);

    const onRowEditInit = () => {
        setShowPIIWarningNotice(true);
    };

    const onRowEditCancel = () => {
        setShowPIIWarningNotice(false);
    };

    const onRowEditComplete = ({ newData }: DataTableRowEditCompleteEvent) => {
        const member = newData as MemberDetails;
        const existingMember = members.find((m) => m.id === member.id);
        if (existingMember == null) return;

        const body: { legalFirstName?: string; legalLastName?: string } = {};
        if (member.legalFirstName !== existingMember.legalFirstName) {
            body.legalFirstName = member.legalFirstName ?? undefined;
        }
        if (member.legalLastName !== existingMember.legalLastName) {
            body.legalLastName = member.legalLastName ?? undefined;
        }
        if (Object.keys(body).length === 0) return;

        setPendingUpdate({
            memberId: member.id,
            legalFirstName: member.legalFirstName,
            legalLastName: member.legalLastName,
        });
    };

    const handleConfirmPIIUpdate = async () => {
        if (pendingUpdate == null) return;
        setIsSubmitting(true);
        try {
            await updateMemberAsync(pendingUpdate.memberId, {
                legalFirstName: pendingUpdate.legalFirstName ?? undefined,
                legalLastName: pendingUpdate.legalLastName ?? undefined,
            });
            toast.show(
                'Official identity record updated!',
                MutationSuccessToastOptions,
            );
            setPendingUpdate(null);
            setShowPIIWarningNotice(false);
        } catch (e) {
            toast.show(
                'Error updating official identity record',
                MutationErrorToastOptions(e),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseConfirm = () => {
        setPendingUpdate(null);
        setShowPIIWarningNotice(false);
    };

    if (members == null || members.length === 0) return null;

    return (
        <YStack gap={12}>
            <H6Emphasized>Official identity record</H6Emphasized>
            {showPIIWarningNotice && (
                <Notice
                    variant="warning"
                    title="Danger zone"
                    description={PII_UPDATE_WARNING}
                    onDismiss={() => {
                        setShowPIIWarningNotice(false);
                    }}
                />
            )}
            {isSubmitting ? (
                <Spinner />
            ) : (
                <DataTable
                    value={members}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: 13 }}
                    editMode="row"
                    onRowEditInit={onRowEditInit}
                    onRowEditCancel={onRowEditCancel}
                    onRowEditComplete={onRowEditComplete}
                    scrollable
                >
                    <Column field="id" header="id" hidden />
                    <Column
                        field="email"
                        header="Member"
                        body={({ email }: MemberDetails) => email}
                        style={{ minWidth: 180 }}
                    />
                    <Column
                        field="legalFirstName"
                        header="Legal first name"
                        editor={
                            canEditMemberPii
                                ? (options) => TextEditor({ options })
                                : null
                        }
                        style={{ minWidth: 150 }}
                    />
                    <Column
                        field="legalLastName"
                        header="Legal last name"
                        editor={
                            canEditMemberPii
                                ? (options) => TextEditor({ options })
                                : null
                        }
                        style={{ minWidth: 150 }}
                    />
                    <Column rowEditor hidden={!canEditMemberPii} />
                </DataTable>
            )}
            <ConfirmModal
                isOpen={pendingUpdate != null}
                onClose={handleCloseConfirm}
                onConfirm={handleConfirmPIIUpdate}
                description="I have completed the proper identity verification steps and am ready to update the official identity record."
                yesText="Yes, update identity record"
                noText="No, hold on..."
            />
        </YStack>
    );
};
